import React, { useState, useEffect, useCallback } from 'react';
import { getData as getSeoContent } from '../../services/seoContentServices';
import {
  getData as getPastEventSeo,
  getpastConfYears,
  getpastWebinarYears,
} from '../../services/pastEventSeoServices';
import { getData as getSchemaMarkup } from '../../services/seoSchemaMarkupServices';
import { getData as getXmlSitemap } from '../../services/seoXmlSitemapServices';
import Header from '../../components/Layout/Header';
import Footer from '../../components/Layout/Footer';
import Sidebar from '../../components/Layout/Sidebar';
import Spinner from '../../components/Spinner';

// Same page-type options as cms/src/pages/pasteventseo/pasteventseo.js —
// kept in sync manually since that file doesn't export them.
const CONFERENCE_PAGE_TYPES = [
  { value: 'report', label: 'Report' },
  { value: 'proceedings', label: 'Proceedings' },
  { value: 'pc_pastaffilation', label: 'Attendees' },
  { value: 'gallery', label: 'Gallery' },
  { value: 'pc_testimonials', label: 'Testimonials' },
];

const WEBINAR_PAGE_TYPES = [
  { value: 'report', label: 'Report' },
  { value: 'proceedings', label: 'Proceedings' },
  { value: 'webinarvideos', label: 'Videos' },
  { value: 'pc_testimonials', label: 'Testimonials' },
];

const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

// 'nov2021' -> { sortKey: 202110, yearOnly: '2021' }. Unrecognized formats
// fall back to sorting last and showing the raw slug, so nothing crashes.
const parseYearSlug = (yearSlug = '') => {
  const match = String(yearSlug).toLowerCase().match(/^([a-z]{3})(\d{4})$/);
  if (!match) return { sortKey: Number.MAX_SAFE_INTEGER, yearOnly: yearSlug };
  const monthIdx = MONTHS.indexOf(match[1]);
  const year = parseInt(match[2], 10);
  if (monthIdx === -1 || Number.isNaN(year)) {
    return { sortKey: Number.MAX_SAFE_INTEGER, yearOnly: yearSlug };
  }
  return { sortKey: year * 100 + monthIdx, yearOnly: match[2] };
};

// ✓ / ✗ symbol cell (colored, no text) — used everywhere in this report.
const Symbol = ({ ok, title }) => (
  <span className={`fs-5 fw-bold ${ok ? 'text-success' : 'text-danger'}`} title={title || (ok ? 'Present' : 'Missing')}>
    {ok ? '✓' : '✗'}
  </span>
);

// Single row in the "File Status" list (sitemap.xml / llms.txt / robots.txt).
// Kept as its own row component so the icon, label, link, and timestamp all
// line up in a consistent grid regardless of label length.
const FileStatusRow = ({ label, fileName, present, updatedAt, buildFileUrl, formatDate }) => (
  <div className="d-flex align-items-center justify-content-between py-2 border-bottom">
    <div className="d-flex align-items-center">
      <span style={{ width: '24px' }} className="text-center">
        <Symbol ok={present} />
      </span>
      <strong className="ms-2">{label}</strong>
      {present && (
        <a
          className="ms-2"
          href={buildFileUrl(fileName)}
          target="_blank"
          rel="noopener noreferrer"
        >
          (Open)
        </a>
      )}
    </div>
    <div className="text-muted small text-end">
      {present ? `Updated ${formatDate(updatedAt)}` : 'Not found'}
    </div>
  </div>
);

const Seoreport = () => {
  const [loading, setLoading] = useState(false);
  const [metaRows, setMetaRows] = useState([]);     // seoContentDtls only: meta title/desc, OG, Twitter
  const [schemaRows, setSchemaRows] = useState([]);  // seoSchemaMarkupDtls only — kept separate, own source
  const [confMatrix, setConfMatrix] = useState({ years: [], data: {} });
  const [webinarMatrix, setWebinarMatrix] = useState({ years: [], data: {} });
  // Sitemap / llms.txt / robots.txt status — rendered directly under the
  // Schema Markup table so all "site-level" SEO checks live in one column.
  const [fileStatus, setFileStatus] = useState({
    xml_file: null,
    llms_file: null,
    robots_file: null,
    updatedAt: null,
  });

  const cms_sub_domain = localStorage.getItem('cms_sub_domain');

  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleString(undefined, {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  };

  const buildFileUrl = (fileName) => {
    if (cms_sub_domain) return `https://${cms_sub_domain}.scientexconference.com/${fileName}`;
    if (window.location.hostname === 'localhost') return `http://localhost:3022/${fileName}`;
    return `/${fileName}`;
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [metaRes, pastEventRes, confYearsRes, webinarYearsRes, schemaRes, sitemapRes] = await Promise.all([
        getSeoContent(),        // array — seoContentDtl rows (meta_title, meta_description, og_*, twitter_*)
        getPastEventSeo(),      // array — existing pastEventSeoDtl rows
        getpastConfYears(),     // [{ year, display_year }]
        getpastWebinarYears(),  // [{ year, display_year }]
        getSchemaMarkup(),      // { status, data, message } -> use .data
        getXmlSitemap(),        // array (1 row expected) — xml_file / llms_file / robots_file
      ]);

      // --- col-6 #1: Meta Title/Description + OG + Twitter, straight from seoContentDtls ---
      const metaList = metaRes || [];
      setMetaRows(
        metaList
          .slice()
          .sort((a, b) => a.page_name.localeCompare(b.page_name))
          .map((m) => ({
            page_name: m.page_name,
            meta_title_ok: !!m.meta_title,
            meta_desc_ok: !!m.meta_description,
            og_ok: !!(m.og_url && m.og_site_name && m.seo_image),
            twitter_ok: !!(m.twitter_card && m.twitter_site && m.seo_image),
          }))
      );

      // --- col-6 #2: Schema Markup, kept fully separate — sourced only
      // from seoSchemaMarkupDtl, not derived/combined with meta data. ---
      const schemaList = schemaRes?.data || [];
      setSchemaRows(
        schemaList
          .slice()
          .sort((a, b) => a.page_name.localeCompare(b.page_name))
          .map((s) => ({
            page_name: s.page_name,
            schema_ok: !!s.conf_name,
            updatedAt: s.updatedAt || null,
          }))
      );

      // --- Past Event SEO: pivot into year x page_type grids. Year column
      // shows the plain year number only (e.g. "2021"), not "nov2021". ---
      const existing = pastEventRes || [];
      const findExisting = (event_type, year, page_type) =>
        existing.find((r) => r.event_type === event_type && r.year === year && r.page_type === page_type);

      const buildMatrix = (yearsRes, eventType, pageTypes) => {
        const years = [...(yearsRes || [])]
          .map((y) => ({ ...y, ...parseYearSlug(y.year) }))
          .sort((a, b) => a.sortKey - b.sortKey);
        const data = {};
        years.forEach((y) => {
          data[y.year] = {};
          pageTypes.forEach((pt) => {
            const row = findExisting(eventType, y.year, pt.value);
            data[y.year][pt.value] = {
              ok: !!row?.meta_title && !!row?.meta_description,
              updatedAt: row?.updatedAt || null,
            };
          });
        });
        return { years, data };
      };

      setConfMatrix(buildMatrix(confYearsRes, 'conference', CONFERENCE_PAGE_TYPES));
      setWebinarMatrix(buildMatrix(webinarYearsRes, 'webinar', WEBINAR_PAGE_TYPES));

      // --- Sitemap.xml / llms.txt / robots.txt status ---
      const sitemapRow = (sitemapRes && sitemapRes[0]) || null;
      setFileStatus({
        xml_file: sitemapRow?.xml_file || null,
        llms_file: sitemapRow?.llms_file || null,
        robots_file: sitemapRow?.robots_file || null,
        updatedAt: sitemapRow?.updatedAt || null,
      });
    } catch (error) {
      console.error('Error building SEO report:', error);
    } finally {
      setTimeout(() => setLoading(false), 400);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const PivotTable = ({ title, matrix, pageTypes }) => (
    <>
      <p className="text-muted small mb-2">{title}</p>
      {matrix.years.length === 0 ? (
        <p className="text-muted mb-0">No years found.</p>
      ) : (
        <div className="table-responsive">
          <table className="table table-bordered table-striped align-middle text-center mb-0">
            <thead>
              <tr>
                <th className="text-start" style={{ minWidth: '80px' }}>Year</th>
                {pageTypes.map((pt) => (
                  <th key={pt.value}>{pt.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {matrix.years.map((y) => (
                <tr key={y.year}>
                  <td className="text-start fw-semibold">{y.yearOnly}</td>
                  {pageTypes.map((pt) => {
                    const cell = matrix.data[y.year]?.[pt.value];
                    return (
                      <td key={pt.value}>
                        <Symbol
                          ok={cell?.ok}
                          title={cell?.ok ? `Updated ${formatDate(cell.updatedAt)}` : 'Meta title/description missing'}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );

  return (
    <div className="d-flex">
      <Sidebar />
      <div className="main-content w-100">
        <Header />
        {loading ? (
          <div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: '300px' }}>
            <Spinner />
          </div>
        ) : (
          <div>
            <div className="container-fluid p-4">
              <p className="mb-3 fs-4">SEO Config <b>/ Report</b></p>

              {/* --- Page-level SEO: col-6 Meta/OG/Twitter | col-6 Schema Markup + File Status --- */}
              <div className="card p-3 mb-4">
                <h6 className="mb-3">Page-level SEO (Meta Tags)</h6>
                <div className="row">
                  <div className="col-md-6 mb-3 mb-md-0">
                    <p className="text-muted small mb-2">Meta Title / Description &amp; Open Graph / Twitter — from Meta Tags (seoContentDtls)</p>
                    <div className="table-responsive">
                      <table className="table table-bordered table-striped align-middle text-center mb-0">
                        <thead>
                          <tr>
                            <th className="text-start">Page</th>
                            <th>Meta Title</th>
                            <th>Meta Desc.</th>
                            <th>OG</th>
                            <th>Twitter</th>
                          </tr>
                        </thead>
                        <tbody>
                          {metaRows.map((row) => (
                            <tr key={row.page_name}>
                              <td className="text-start">{row.page_name}</td>
                              <td><Symbol ok={row.meta_title_ok} /></td>
                              <td><Symbol ok={row.meta_desc_ok} /></td>
                              <td><Symbol ok={row.og_ok} /></td>
                              <td><Symbol ok={row.twitter_ok} /></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <p className="text-muted small mb-2">Schema Markup — from Schema Markup table (seoSchemaMarkupDtls)</p>
                    <div className="table-responsive">
                      <table className="table table-bordered table-striped align-middle text-center mb-0">
                        <thead>
                          <tr>
                            <th className="text-start">Page</th>
                            <th>Schema</th>
                            <th className="text-start">Last Updated</th>
                          </tr>
                        </thead>
                        <tbody>
                          {schemaRows.map((row) => (
                            <tr key={row.page_name}>
                              <td className="text-start">{row.page_name}</td>
                              <td><Symbol ok={row.schema_ok} /></td>
                              <td className="text-start">{formatDate(row.updatedAt)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* File Status now lives directly under Schema Markup,
                        in the same column, instead of a separate full-width
                        section below both tables. */}
                    <p className="text-muted small mb-2 mt-4">File Status</p>
                    <div className="border rounded px-3">
                      <FileStatusRow
                        label="sitemap.xml"
                        fileName="sitemap.xml"
                        present={!!fileStatus.xml_file}
                        updatedAt={fileStatus.updatedAt}
                        buildFileUrl={buildFileUrl}
                        formatDate={formatDate}
                      />
                      <FileStatusRow
                        label="llms.txt"
                        fileName="llms.txt"
                        present={!!fileStatus.llms_file}
                        updatedAt={fileStatus.updatedAt}
                        buildFileUrl={buildFileUrl}
                        formatDate={formatDate}
                      />
                      <FileStatusRow
                        label="robots.txt"
                        fileName="robots.txt"
                        present={!!fileStatus.robots_file}
                        updatedAt={fileStatus.updatedAt}
                        buildFileUrl={buildFileUrl}
                        formatDate={formatDate}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* --- Past Conference (col-6) | Past Webinar (col-6), side by side --- */}
              <div className="card p-3 mb-4">
                <h6 className="mb-3">Past Event SEO</h6>
                <div className="row">
                  <div className="col-md-6 mb-3 mb-md-0">
                    <PivotTable title="Past Conference" matrix={confMatrix} pageTypes={CONFERENCE_PAGE_TYPES} />
                  </div>
                  <div className="col-md-6">
                    <PivotTable title="Past Webinar" matrix={webinarMatrix} pageTypes={WEBINAR_PAGE_TYPES} />
                  </div>
                </div>
              </div>
            </div>
            <Footer />
          </div>
        )}
      </div>
    </div>
  );
};

export default Seoreport;